// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/** Spacing, joiners, and soft hyphen. See each member for what appears on screen. */
export enum GlyphIdSpacingInvisible {
  /**
   * Renders like a normal space; keeps adjacent words on one line (non-breaking
   * space).
   */
  Nbsp = 'nbsp',
  /** Blank band one en wide (typically half an em in the current font). */
  Ensp = 'ensp',
  /** Blank band one em wide (full em in the current font). */
  Emsp = 'emsp',
  /** Narrow blank band (thinner than a normal space). */
  Thinsp = 'thinsp',
  /** No visible mark; stops letters from joining (e.g. in Arabic scripts). */
  Zwnj = 'zwnj',
  /** No visible mark; encourages joining/ligatures where the font supports it. */
  Zwj = 'zwj',
  /** Usually invisible; may show a hyphen only where a line can break. */
  Shy = 'shy',
}

/** Dashes, ellipsis, bullet, and middle dot—always visible punctuation. */
export enum GlyphIdDashesPunctuation {
  /** Long dash — (em dash). */
  Mdash = 'mdash',
  /** Medium dash – (en dash). */
  Ndash = 'ndash',
  /** Three dots … (horizontal ellipsis). */
  Hellip = 'hellip',
  /** Filled round bullet •. */
  Bull = 'bull',
  /** Raised dot · (middle dot). */
  Middot = 'middot',
}

/** Curly/smart quotes and angle quotation marks. */
export enum GlyphIdQuotations {
  /** Opening double quote “. */
  Ldquo = 'ldquo',
  /** Closing double quote ”. */
  Rdquo = 'rdquo',
  /** Opening single quote ‘. */
  Lsquo = 'lsquo',
  /** Closing single quote / apostrophe ’. */
  Rsquo = 'rsquo',
  /** Low double quote „. */
  Bdquo = 'bdquo',
  /** Left guillemet «. */
  Laquo = 'laquo',
  /** Right guillemet ». */
  Raquo = 'raquo',
  /** Single left guillemet ‹. */
  Lsaquo = 'lsaquo',
  /** Single right guillemet ›. */
  Rsaquo = 'rsaquo',
}

/** Literal markup characters (what you see is the symbol itself). */
export enum GlyphIdReservedMarkup {
  /** Ampersand &. */
  Amp = 'amp',
  /** Less-than <. */
  Lt = 'lt',
  /** Greater-than >. */
  Gt = 'gt',
  /** Straight double quote ". */
  Quot = 'quot',
  /** Apostrophe / straight single quote '. */
  Apos = 'apos',
}

/** Copyright, registered, and trademark signs. */
export enum GlyphIdLegalTrademark {
  /** Copyright sign ©. */
  Copy = 'copy',
  /** Registered sign ®. */
  Reg = 'reg',
  /** Trademark sign ™. */
  Trade = 'trade',
}

/** Common currency symbols (glyph shape follows the font). */
export enum GlyphIdCurrency {
  /** Euro €. */
  Euro = 'euro',
  /** Pound £. */
  Pound = 'pound',
  /** Yen / yuan ¥. */
  Yen = 'yen',
  /** Cent ¢. */
  Cent = 'cent',
  /** Generic currency ¤. */
  Curren = 'curren',
}

/** Math operators, comparisons, roots, fractions, degree, per mille. */
export enum GlyphIdMathematical {
  /** Multiplication sign ×. */
  Times = 'times',
  /** Division sign ÷. */
  Divide = 'divide',
  /** Plus-minus ±. */
  Plusmn = 'plusmn',
  /** Minus sign − (often wider than a hyphen). */
  Minus = 'minus',
  /** Infinity ∞. */
  Infin = 'infin',
  /** Not equal ≠. */
  Ne = 'ne',
  /** Less than or equal ≤. */
  Le = 'le',
  /** Greater than or equal ≥. */
  Ge = 'ge',
  /** Approximately equal ≈. */
  Asymp = 'asymp',
  /** Square root √ (radical without overbar extension in plain text). */
  Radic = 'radic',
  /** Superscript two ². */
  Sup2 = 'sup2',
  /** Superscript three ³. */
  Sup3 = 'sup3',
  /** Vulgar fraction one quarter ¼. */
  Frac14 = 'frac14',
  /** Vulgar fraction one half ½. */
  Frac12 = 'frac12',
  /** Vulgar fraction three quarters ¾. */
  Frac34 = 'frac34',
  /** Degree sign °. */
  Deg = 'deg',
  /** Per mille ‰. */
  Permil = 'permil',
}

/** Single- and double-stroke arrows. */
export enum GlyphIdArrows {
  /** Left arrow ←. */
  Larr = 'larr',
  /** Right arrow →. */
  Rarr = 'rarr',
  /** Up arrow ↑. */
  Uarr = 'uarr',
  /** Down arrow ↓. */
  Darr = 'darr',
  /** Left-right arrow ↔. */
  Harr = 'harr',
  /** Double left arrow ⇐. */
  LArr = 'lArr',
  /** Double right arrow ⇒. */
  RArr = 'rArr',
  /** Left-right double arrow ⇔. */
  HArr = 'hArr',
}

/** Playing-card suit symbols (suit pips). */
export enum GlyphIdCardSuits {
  /** Spade suit ♠. */
  Spades = 'spades',
  /** Club suit ♣. */
  Clubs = 'clubs',
  /** Heart suit ♥. */
  Hearts = 'hearts',
  /** Diamond suit ♦. */
  Diams = 'diams',
}

/** Greek lowercase letters (classical glyph per font). */
export enum GlyphIdGreekLowercase {
  /** Α */
  Alpha = 'alpha',
  /** Β */
  Beta = 'beta',
  /** Γ */
  Gamma = 'gamma',
  /** Δ */
  Delta = 'delta',
  /** Ε */
  Epsilon = 'epsilon',
  /** Π */
  Pi = 'pi',
  /** Σ */
  Sigma = 'sigma',
  /** Ω */
  Omega = 'omega',
  /** Θ */
  Theta = 'theta',
  /** Λ */
  Lambda = 'lambda',
  /** Μ */
  Mu = 'mu',
  /** Φ */
  Phi = 'phi',
  /** Ψ */
  Psi = 'psi',
}

/**
 * Greek uppercase letters. Enum **values** are the HTML tokens `Alpha`, `Beta`, …
 * (capitalized), not the lowercase spellings.
 */
export enum GlyphIdGreekUppercase {
  /** Α */
  AlphaUpper = 'Alpha',
  /** Β */
  BetaUpper = 'Beta',
  /** Γ */
  GammaUpper = 'Gamma',
  /** Δ */
  DeltaUpper = 'Delta',
  /** Π */
  PiUpper = 'Pi',
  /** Σ */
  SigmaUpper = 'Sigma',
  /** Ω */
  OmegaUpper = 'Omega',
  /** Θ */
  ThetaUpper = 'Theta',
  /** Λ */
  LambdaUpper = 'Lambda',
  /** Φ */
  PhiUpper = 'Phi',
  /** Ψ */
  PsiUpper = 'Psi',
}

/**
 * Hex digit string for each scalar—**same on-screen character** as the matching
 * {@link GlyphIdSpacingInvisible} member. Prefer member docs on the name enum for
 * what users see; use `parseInt(hex, 16)` before `String.fromCodePoint`.
 *
 * @see {@link GlyphIdSpacingInvisible}
 */
export enum GlyphHexSpacingInvisible {
  /** Non-breaking space (like a space). @see {@link GlyphIdSpacingInvisible.Nbsp} */
  Nbsp = '00A0',
  /** Blank band one en wide. @see {@link GlyphIdSpacingInvisible.Ensp} */
  Ensp = '2002',
  /** Blank band one em wide. @see {@link GlyphIdSpacingInvisible.Emsp} */
  Emsp = '2003',
  /** Narrow blank band. @see {@link GlyphIdSpacingInvisible.Thinsp} */
  Thinsp = '2009',
  /** No visible mark (joining control). @see {@link GlyphIdSpacingInvisible.Zwnj} */
  Zwnj = '200C',
  /** No visible mark (joining control). @see {@link GlyphIdSpacingInvisible.Zwj} */
  Zwj = '200D',
  /** Soft hyphen (often invisible). @see {@link GlyphIdSpacingInvisible.Shy} */
  Shy = '00AD',
}

/** Same characters as {@link GlyphIdDashesPunctuation} (see there for glyphs). */
export enum GlyphHexDashesPunctuation {
  /** Long dash —. @see {@link GlyphIdDashesPunctuation.Mdash} */
  Mdash = '2014',
  /** Medium dash –. @see {@link GlyphIdDashesPunctuation.Ndash} */
  Ndash = '2013',
  /** Ellipsis …. @see {@link GlyphIdDashesPunctuation.Hellip} */
  Hellip = '2026',
  /** Bullet •. @see {@link GlyphIdDashesPunctuation.Bull} */
  Bull = '2022',
  /** Middle dot ·. @see {@link GlyphIdDashesPunctuation.Middot} */
  Middot = '00B7',
}

/** Same characters as {@link GlyphIdQuotations}. */
export enum GlyphHexQuotations {
  /** Opening double “. @see {@link GlyphIdQuotations.Ldquo} */
  Ldquo = '201C',
  /** Closing double ”. @see {@link GlyphIdQuotations.Rdquo} */
  Rdquo = '201D',
  /** Opening single ‘. @see {@link GlyphIdQuotations.Lsquo} */
  Lsquo = '2018',
  /** Closing single ’. @see {@link GlyphIdQuotations.Rsquo} */
  Rsquo = '2019',
  /** Low double „. @see {@link GlyphIdQuotations.Bdquo} */
  Bdquo = '201E',
  /** Left guillemet «. @see {@link GlyphIdQuotations.Laquo} */
  Laquo = '00AB',
  /** Right guillemet ». @see {@link GlyphIdQuotations.Raquo} */
  Raquo = '00BB',
  /** Single «-style ‹. @see {@link GlyphIdQuotations.Lsaquo} */
  Lsaquo = '2039',
  /** Single »-style ›. @see {@link GlyphIdQuotations.Rsaquo} */
  Rsaquo = '203A',
}

/** Same characters as {@link GlyphIdReservedMarkup}. */
export enum GlyphHexReservedMarkup {
  /** &. @see {@link GlyphIdReservedMarkup.Amp} */
  Amp = '0026',
  /** <. @see {@link GlyphIdReservedMarkup.Lt} */
  Lt = '003C',
  /** > . @see {@link GlyphIdReservedMarkup.Gt} */
  Gt = '003E',
  /** ". @see {@link GlyphIdReservedMarkup.Quot} */
  Quot = '0022',
  /** '. @see {@link GlyphIdReservedMarkup.Apos} */
  Apos = '0027',
}

/** Same characters as {@link GlyphIdLegalTrademark}. */
export enum GlyphHexLegalTrademark {
  /** ©. @see {@link GlyphIdLegalTrademark.Copy} */
  Copy = '00A9',
  /** ®. @see {@link GlyphIdLegalTrademark.Reg} */
  Reg = '00AE',
  /** ™. @see {@link GlyphIdLegalTrademark.Trade} */
  Trade = '2122',
}

/** Same characters as {@link GlyphIdCurrency}. */
export enum GlyphHexCurrency {
  /** €. @see {@link GlyphIdCurrency.Euro} */
  Euro = '20AC',
  /** £. @see {@link GlyphIdCurrency.Pound} */
  Pound = '00A3',
  /** ¥. @see {@link GlyphIdCurrency.Yen} */
  Yen = '00A5',
  /** ¢. @see {@link GlyphIdCurrency.Cent} */
  Cent = '00A2',
  /** ¤. @see {@link GlyphIdCurrency.Curren} */
  Curren = '00A4',
}

/** Same characters as {@link GlyphIdMathematical}. */
export enum GlyphHexMathematical {
  /** ×. @see {@link GlyphIdMathematical.Times} */
  Times = '00D7',
  /** ÷. @see {@link GlyphIdMathematical.Divide} */
  Divide = '00F7',
  /** ±. @see {@link GlyphIdMathematical.Plusmn} */
  Plusmn = '00B1',
  /** −. @see {@link GlyphIdMathematical.Minus} */
  Minus = '2212',
  /** ∞. @see {@link GlyphIdMathematical.Infin} */
  Infin = '221E',
  /** ≠. @see {@link GlyphIdMathematical.Ne} */
  Ne = '2260',
  /** ≤. @see {@link GlyphIdMathematical.Le} */
  Le = '2264',
  /** ≥. @see {@link GlyphIdMathematical.Ge} */
  Ge = '2265',
  /** ≈. @see {@link GlyphIdMathematical.Asymp} */
  Asymp = '2248',
  /** √. @see {@link GlyphIdMathematical.Radic} */
  Radic = '221A',
  /** ². @see {@link GlyphIdMathematical.Sup2} */
  Sup2 = '00B2',
  /** ³. @see {@link GlyphIdMathematical.Sup3} */
  Sup3 = '00B3',
  /** ¼. @see {@link GlyphIdMathematical.Frac14} */
  Frac14 = '00BC',
  /** ½. @see {@link GlyphIdMathematical.Frac12} */
  Frac12 = '00BD',
  /** ¾. @see {@link GlyphIdMathematical.Frac34} */
  Frac34 = '00BE',
  /** °. @see {@link GlyphIdMathematical.Deg} */
  Deg = '00B0',
  /** ‰. @see {@link GlyphIdMathematical.Permil} */
  Permil = '2030',
}

/** Same characters as {@link GlyphIdArrows}. */
export enum GlyphHexArrows {
  /** ←. @see {@link GlyphIdArrows.Larr} */
  Larr = '2190',
  /** →. @see {@link GlyphIdArrows.Rarr} */
  Rarr = '2192',
  /** ↑. @see {@link GlyphIdArrows.Uarr} */
  Uarr = '2191',
  /** ↓. @see {@link GlyphIdArrows.Darr} */
  Darr = '2193',
  /** ↔. @see {@link GlyphIdArrows.Harr} */
  Harr = '2194',
  /** ⇐. @see {@link GlyphIdArrows.LArr} */
  LArr = '21D0',
  /** ⇒. @see {@link GlyphIdArrows.RArr} */
  RArr = '21D2',
  /** ⇔. @see {@link GlyphIdArrows.HArr} */
  HArr = '21D4',
}

/** Same characters as {@link GlyphIdCardSuits}. */
export enum GlyphHexCardSuits {
  /** ♠. @see {@link GlyphIdCardSuits.Spades} */
  Spades = '2660',
  /** ♣. @see {@link GlyphIdCardSuits.Clubs} */
  Clubs = '2663',
  /** ♥. @see {@link GlyphIdCardSuits.Hearts} */
  Hearts = '2665',
  /** ♦. @see {@link GlyphIdCardSuits.Diams} */
  Diams = '2666',
}

/** Same characters as {@link GlyphIdGreekLowercase}. */
export enum GlyphHexGreekLowercase {
  /** Α. @see {@link GlyphIdGreekLowercase.Alpha} */
  Alpha = '03B1',
  /** Β. @see {@link GlyphIdGreekLowercase.Beta} */
  Beta = '03B2',
  /** Γ. @see {@link GlyphIdGreekLowercase.Gamma} */
  Gamma = '03B3',
  /** Δ. @see {@link GlyphIdGreekLowercase.Delta} */
  Delta = '03B4',
  /** Ε. @see {@link GlyphIdGreekLowercase.Epsilon} */
  Epsilon = '03B5',
  /** Π. @see {@link GlyphIdGreekLowercase.Pi} */
  Pi = '03C0',
  /** Σ. @see {@link GlyphIdGreekLowercase.Sigma} */
  Sigma = '03C3',
  /** Ω. @see {@link GlyphIdGreekLowercase.Omega} */
  Omega = '03C9',
  /** Θ. @see {@link GlyphIdGreekLowercase.Theta} */
  Theta = '03B8',
  /** Λ. @see {@link GlyphIdGreekLowercase.Lambda} */
  Lambda = '03BB',
  /** Μ. @see {@link GlyphIdGreekLowercase.Mu} */
  Mu = '03BC',
  /** Φ. @see {@link GlyphIdGreekLowercase.Phi} */
  Phi = '03C6',
  /** Ψ. @see {@link GlyphIdGreekLowercase.Psi} */
  Psi = '03C8',
}

/** Same characters as {@link GlyphIdGreekUppercase}. */
export enum GlyphHexGreekUppercase {
  /** Α. @see {@link GlyphIdGreekUppercase.AlphaUpper} */
  AlphaUpper = '0391',
  /** Β. @see {@link GlyphIdGreekUppercase.BetaUpper} */
  BetaUpper = '0392',
  /** Γ. @see {@link GlyphIdGreekUppercase.GammaUpper} */
  GammaUpper = '0393',
  /** Δ. @see {@link GlyphIdGreekUppercase.DeltaUpper} */
  DeltaUpper = '0394',
  /** Π. @see {@link GlyphIdGreekUppercase.PiUpper} */
  PiUpper = '03A0',
  /** Σ. @see {@link GlyphIdGreekUppercase.SigmaUpper} */
  SigmaUpper = '03A3',
  /** Ω. @see {@link GlyphIdGreekUppercase.OmegaUpper} */
  OmegaUpper = '03A9',
  /** Θ. @see {@link GlyphIdGreekUppercase.ThetaUpper} */
  ThetaUpper = '0398',
  /** Λ. @see {@link GlyphIdGreekUppercase.LambdaUpper} */
  LambdaUpper = '039B',
  /** Φ. @see {@link GlyphIdGreekUppercase.PhiUpper} */
  PhiUpper = '03A6',
  /** Ψ. @see {@link GlyphIdGreekUppercase.PsiUpper} */
  PsiUpper = '03A8',
}

/**
 * Decimal code point—**same visible character** as {@link GlyphIdSpacingInvisible}.
 * Use `String.fromCodePoint(n)` to preview the glyph in devtools or tests.
 *
 * @example
 *
 * ```ts
 * String.fromCodePoint(GlyphDecimalSpacingInvisible.Nbsp); // non-breaking space
 * ```
 *
 * @see {@link GlyphIdSpacingInvisible}
 */
export enum GlyphDecimalSpacingInvisible {
  /** Non-breaking space (like a space). @see {@link GlyphIdSpacingInvisible.Nbsp} */
  Nbsp = 160,
  /** Blank band one en wide. @see {@link GlyphIdSpacingInvisible.Ensp} */
  Ensp = 8194,
  /** Blank band one em wide. @see {@link GlyphIdSpacingInvisible.Emsp} */
  Emsp = 8195,
  /** Narrow blank band. @see {@link GlyphIdSpacingInvisible.Thinsp} */
  Thinsp = 8201,
  /** No visible mark (joining control). @see {@link GlyphIdSpacingInvisible.Zwnj} */
  Zwnj = 8204,
  /** No visible mark (joining control). @see {@link GlyphIdSpacingInvisible.Zwj} */
  Zwj = 8205,
  /** Soft hyphen (often invisible). @see {@link GlyphIdSpacingInvisible.Shy} */
  Shy = 173,
}

/** Same characters as {@link GlyphIdDashesPunctuation}. */
export enum GlyphDecimalDashesPunctuation {
  /** Long dash —. @see {@link GlyphIdDashesPunctuation.Mdash} */
  Mdash = 8212,
  /** Medium dash –. @see {@link GlyphIdDashesPunctuation.Ndash} */
  Ndash = 8211,
  /** Ellipsis …. @see {@link GlyphIdDashesPunctuation.Hellip} */
  Hellip = 8230,
  /** Bullet •. @see {@link GlyphIdDashesPunctuation.Bull} */
  Bull = 8226,
  /** Middle dot ·. @see {@link GlyphIdDashesPunctuation.Middot} */
  Middot = 183,
}

/** Same characters as {@link GlyphIdQuotations}. */
export enum GlyphDecimalQuotations {
  /** Opening double “. @see {@link GlyphIdQuotations.Ldquo} */
  Ldquo = 8220,
  /** Closing double ”. @see {@link GlyphIdQuotations.Rdquo} */
  Rdquo = 8221,
  /** Opening single ‘. @see {@link GlyphIdQuotations.Lsquo} */
  Lsquo = 8216,
  /** Closing single ’. @see {@link GlyphIdQuotations.Rsquo} */
  Rsquo = 8217,
  /** Low double „. @see {@link GlyphIdQuotations.Bdquo} */
  Bdquo = 8222,
  /** Left guillemet «. @see {@link GlyphIdQuotations.Laquo} */
  Laquo = 171,
  /** Right guillemet ». @see {@link GlyphIdQuotations.Raquo} */
  Raquo = 187,
  /** Single ‹. @see {@link GlyphIdQuotations.Lsaquo} */
  Lsaquo = 8249,
  /** Single ›. @see {@link GlyphIdQuotations.Rsaquo} */
  Rsaquo = 8250,
}

/** Same characters as {@link GlyphIdReservedMarkup}. */
export enum GlyphDecimalReservedMarkup {
  /** &. @see {@link GlyphIdReservedMarkup.Amp} */
  Amp = 38,
  /** <. @see {@link GlyphIdReservedMarkup.Lt} */
  Lt = 60,
  /** > . @see {@link GlyphIdReservedMarkup.Gt} */
  Gt = 62,
  /** ". @see {@link GlyphIdReservedMarkup.Quot} */
  Quot = 34,
  /** '. @see {@link GlyphIdReservedMarkup.Apos} */
  Apos = 39,
}

/** Same characters as {@link GlyphIdLegalTrademark}. */
export enum GlyphDecimalLegalTrademark {
  /** ©. @see {@link GlyphIdLegalTrademark.Copy} */
  Copy = 169,
  /** ®. @see {@link GlyphIdLegalTrademark.Reg} */
  Reg = 174,
  /** ™. @see {@link GlyphIdLegalTrademark.Trade} */
  Trade = 8482,
}

/** Same characters as {@link GlyphIdCurrency}. */
export enum GlyphDecimalCurrency {
  /** €. @see {@link GlyphIdCurrency.Euro} */
  Euro = 8364,
  /** £. @see {@link GlyphIdCurrency.Pound} */
  Pound = 163,
  /** ¥. @see {@link GlyphIdCurrency.Yen} */
  Yen = 165,
  /** ¢. @see {@link GlyphIdCurrency.Cent} */
  Cent = 162,
  /** ¤. @see {@link GlyphIdCurrency.Curren} */
  Curren = 164,
}

/** Same characters as {@link GlyphIdMathematical}. */
export enum GlyphDecimalMathematical {
  /** ×. @see {@link GlyphIdMathematical.Times} */
  Times = 215,
  /** ÷. @see {@link GlyphIdMathematical.Divide} */
  Divide = 247,
  /** ±. @see {@link GlyphIdMathematical.Plusmn} */
  Plusmn = 177,
  /** −. @see {@link GlyphIdMathematical.Minus} */
  Minus = 8722,
  /** ∞. @see {@link GlyphIdMathematical.Infin} */
  Infin = 8734,
  /** ≠. @see {@link GlyphIdMathematical.Ne} */
  Ne = 8800,
  /** ≤. @see {@link GlyphIdMathematical.Le} */
  Le = 8804,
  /** ≥. @see {@link GlyphIdMathematical.Ge} */
  Ge = 8805,
  /** ≈. @see {@link GlyphIdMathematical.Asymp} */
  Asymp = 8776,
  /** √. @see {@link GlyphIdMathematical.Radic} */
  Radic = 8730,
  /** ². @see {@link GlyphIdMathematical.Sup2} */
  Sup2 = 178,
  /** ³. @see {@link GlyphIdMathematical.Sup3} */
  Sup3 = 179,
  /** ¼. @see {@link GlyphIdMathematical.Frac14} */
  Frac14 = 188,
  /** ½. @see {@link GlyphIdMathematical.Frac12} */
  Frac12 = 189,
  /** ¾. @see {@link GlyphIdMathematical.Frac34} */
  Frac34 = 190,
  /** °. @see {@link GlyphIdMathematical.Deg} */
  Deg = 176,
  /** ‰. @see {@link GlyphIdMathematical.Permil} */
  Permil = 8240,
}

/** Same characters as {@link GlyphIdArrows}. */
export enum GlyphDecimalArrows {
  /** ←. @see {@link GlyphIdArrows.Larr} */
  Larr = 8592,
  /** →. @see {@link GlyphIdArrows.Rarr} */
  Rarr = 8594,
  /** ↑. @see {@link GlyphIdArrows.Uarr} */
  Uarr = 8593,
  /** ↓. @see {@link GlyphIdArrows.Darr} */
  Darr = 8595,
  /** ↔. @see {@link GlyphIdArrows.Harr} */
  Harr = 8596,
  /** ⇐. @see {@link GlyphIdArrows.LArr} */
  LArr = 8656,
  /** ⇒. @see {@link GlyphIdArrows.RArr} */
  RArr = 8658,
  /** ⇔. @see {@link GlyphIdArrows.HArr} */
  HArr = 8660,
}

/** Same characters as {@link GlyphIdCardSuits}. */
export enum GlyphDecimalCardSuits {
  /** ♠. @see {@link GlyphIdCardSuits.Spades} */
  Spades = 9824,
  /** ♣. @see {@link GlyphIdCardSuits.Clubs} */
  Clubs = 9827,
  /** ♥. @see {@link GlyphIdCardSuits.Hearts} */
  Hearts = 9829,
  /** ♦. @see {@link GlyphIdCardSuits.Diams} */
  Diams = 9830,
}

/** Same characters as {@link GlyphIdGreekLowercase}. */
export enum GlyphDecimalGreekLowercase {
  /** Α. @see {@link GlyphIdGreekLowercase.Alpha} */
  Alpha = 945,
  /** Β. @see {@link GlyphIdGreekLowercase.Beta} */
  Beta = 946,
  /** Γ. @see {@link GlyphIdGreekLowercase.Gamma} */
  Gamma = 947,
  /** Δ. @see {@link GlyphIdGreekLowercase.Delta} */
  Delta = 948,
  /** Ε. @see {@link GlyphIdGreekLowercase.Epsilon} */
  Epsilon = 949,
  /** Π. @see {@link GlyphIdGreekLowercase.Pi} */
  Pi = 960,
  /** Σ. @see {@link GlyphIdGreekLowercase.Sigma} */
  Sigma = 963,
  /** Ω. @see {@link GlyphIdGreekLowercase.Omega} */
  Omega = 969,
  /** Θ. @see {@link GlyphIdGreekLowercase.Theta} */
  Theta = 952,
  /** Λ. @see {@link GlyphIdGreekLowercase.Lambda} */
  Lambda = 955,
  /** Μ. @see {@link GlyphIdGreekLowercase.Mu} */
  Mu = 956,
  /** Φ. @see {@link GlyphIdGreekLowercase.Phi} */
  Phi = 966,
  /** Ψ. @see {@link GlyphIdGreekLowercase.Psi} */
  Psi = 968,
}

/** Same characters as {@link GlyphIdGreekUppercase}. */
export enum GlyphDecimalGreekUppercase {
  /** Α. @see {@link GlyphIdGreekUppercase.AlphaUpper} */
  AlphaUpper = 913,
  /** Β. @see {@link GlyphIdGreekUppercase.BetaUpper} */
  BetaUpper = 914,
  /** Γ. @see {@link GlyphIdGreekUppercase.GammaUpper} */
  GammaUpper = 915,
  /** Δ. @see {@link GlyphIdGreekUppercase.DeltaUpper} */
  DeltaUpper = 916,
  /** Π. @see {@link GlyphIdGreekUppercase.PiUpper} */
  PiUpper = 928,
  /** Σ. @see {@link GlyphIdGreekUppercase.SigmaUpper} */
  SigmaUpper = 931,
  /** Ω. @see {@link GlyphIdGreekUppercase.OmegaUpper} */
  OmegaUpper = 937,
  /** Θ. @see {@link GlyphIdGreekUppercase.ThetaUpper} */
  ThetaUpper = 920,
  /** Λ. @see {@link GlyphIdGreekUppercase.LambdaUpper} */
  LambdaUpper = 923,
  /** Φ. @see {@link GlyphIdGreekUppercase.PhiUpper} */
  PhiUpper = 934,
  /** Ψ. @see {@link GlyphIdGreekUppercase.PsiUpper} */
  PsiUpper = 936,
}

/**
 * Friendly **keys** → canonical **name** token (the `name` in `&name;`). Several
 * keys can share the same string value (same Unicode output after decode).
 *
 * @remarks
 * Use when the UI stores human-facing labels (`Blank`, `CopyrightSign`) instead of
 * raw entity tokens. Values are canonical name tokens suitable for named character
 * references.
 * @remarks
 * Values reference {@link GlyphId*} enum members so aliases stay aligned with the
 * grouped name registries.
 * @example Map alias to token, then preview the glyph
 *
 * ```ts
 * const token = GlyphNames.Blank; // 'nbsp'
 * const char = String.fromCodePoint(GlyphDecimalSpacingInvisible.Nbsp); // NBSP (like a space)
 * // In HTML, `&` + token + `;` is the named reference for that character.
 * ```
 *
 * @example Same glyph, different alias keys
 *
 * ```ts
 * GlyphNames.Dots === GlyphNames.ThreeAsciiDots; // both → hellip → "…"
 * ```
 */
export const GlyphNames = {
  Blank: GlyphIdSpacingInvisible.Nbsp,
  NonBreakingSpaceSpelled: GlyphIdSpacingInvisible.Nbsp,
  NoBreakSpace: GlyphIdSpacingInvisible.Nbsp,
  Nobreak: GlyphIdSpacingInvisible.Nbsp,
  BlankSpace: GlyphIdSpacingInvisible.Ensp,
  SingleAsciiSpace: GlyphIdSpacingInvisible.Ensp,
  WhiteSpace: GlyphIdSpacingInvisible.Ensp,
  Whitespace: GlyphIdSpacingInvisible.Ensp,
  WideSpace: GlyphIdSpacingInvisible.Emsp,
  NarrowSpace: GlyphIdSpacingInvisible.Thinsp,
  OptionalHyphen: GlyphIdSpacingInvisible.Shy,
  LongDash: GlyphIdDashesPunctuation.Mdash,
  EmDashGlyph: GlyphIdDashesPunctuation.Mdash,
  RangeDash: GlyphIdDashesPunctuation.Ndash,
  EnDashGlyph: GlyphIdDashesPunctuation.Ndash,
  Dots: GlyphIdDashesPunctuation.Hellip,
  ThreeDots: GlyphIdDashesPunctuation.Hellip,
  ThreeAsciiDots: GlyphIdDashesPunctuation.Hellip,
  Dot: GlyphIdDashesPunctuation.Bull,
  BulletGlyph: GlyphIdDashesPunctuation.Bull,
  Interpunct: GlyphIdDashesPunctuation.Middot,
  MiddleDotGlyph: GlyphIdDashesPunctuation.Middot,
  OpenQuote: GlyphIdQuotations.Ldquo,
  LeftQuote: GlyphIdQuotations.Ldquo,
  CloseQuote: GlyphIdQuotations.Rdquo,
  RightQuote: GlyphIdQuotations.Rdquo,
  GuillemetLeft: GlyphIdQuotations.Laquo,
  GuillemetRight: GlyphIdQuotations.Raquo,
  CopyrightParen: GlyphIdLegalTrademark.Copy,
  CopyrightSign: GlyphIdLegalTrademark.Copy,
  RegisteredParen: GlyphIdLegalTrademark.Reg,
  RegisteredSign: GlyphIdLegalTrademark.Reg,
  TrademarkParen: GlyphIdLegalTrademark.Trade,
  TrademarkSign: GlyphIdLegalTrademark.Trade,
  PoundSterling: GlyphIdCurrency.Pound,
  Cross: GlyphIdMathematical.Times,
  PlusMinusSign: GlyphIdMathematical.Plusmn,
  InfinitySign: GlyphIdMathematical.Infin,
  AlmostEqual: GlyphIdMathematical.Asymp,
  Radical: GlyphIdMathematical.Radic,
  LeftArrow: GlyphIdArrows.Larr,
  ArrowLeft: GlyphIdArrows.Larr,
  RightArrow: GlyphIdArrows.Rarr,
  ArrowRight: GlyphIdArrows.Rarr,
  UpArrow: GlyphIdArrows.Uarr,
  ArrowUp: GlyphIdArrows.Uarr,
  DownArrow: GlyphIdArrows.Darr,
  ArrowDown: GlyphIdArrows.Darr,
  BidirectionalArrow: GlyphIdArrows.Harr,
  Implies: GlyphIdArrows.RArr,
  DoubleArrowRight: GlyphIdArrows.RArr,
  DoubleArrowLeft: GlyphIdArrows.LArr,
} as const;

/**
 * Keys of {@link GlyphNames} (`Blank`, `LongDash`, …).
 *
 * @example
 *
 * ```ts
 * const key: GlyphName = 'CopyrightSign';
 * const token = GlyphNames[key]; // 'copy' → "©" when decoded
 * ```
 */
export type GlyphName = keyof typeof GlyphNames;

/**
 * Canonical name **token** strings produced by {@link GlyphNames} values — a subset
 * of the string literals in {@link GlyphId} group enums.
 *
 * @example
 *
 * ```ts
 * const v: GlyphNameValue = GlyphNames.InfinitySign; // 'infin' → "∞"
 * ```
 */
export type GlyphNameValue = (typeof GlyphNames)[GlyphName];

/**
 * Every supported HTML entity **name** string (union of all {@link GlyphId*} enums):
 * the `name` segment in `&name;`.
 *
 * @example
 *
 * ```ts
 * const token: GlyphId = GlyphIdMathematical.Infin; // 'infin' → "∞"
 * ```
 */
export type GlyphId =
  | GlyphIdSpacingInvisible
  | GlyphIdDashesPunctuation
  | GlyphIdQuotations
  | GlyphIdReservedMarkup
  | GlyphIdLegalTrademark
  | GlyphIdCurrency
  | GlyphIdMathematical
  | GlyphIdArrows
  | GlyphIdCardSuits
  | GlyphIdGreekLowercase
  | GlyphIdGreekUppercase;

/**
 * Every supported **hex** scalar digit string (union of all {@link GlyphHex*}
 * enums). Compare after normalizing dynamic input (strip `&#x`, `;`, etc.).
 *
 * @example
 *
 * ```ts
 * const h: GlyphHex = GlyphHexLegalTrademark.Copy; // '00A9' → "©"
 * ```
 */
export type GlyphHex =
  | GlyphHexSpacingInvisible
  | GlyphHexDashesPunctuation
  | GlyphHexQuotations
  | GlyphHexReservedMarkup
  | GlyphHexLegalTrademark
  | GlyphHexCurrency
  | GlyphHexMathematical
  | GlyphHexArrows
  | GlyphHexCardSuits
  | GlyphHexGreekLowercase
  | GlyphHexGreekUppercase;

/**
 * Every supported **decimal** Unicode code point (union of all {@link GlyphDecimal*}
 * enums). Matches `&#n;` without delimiters.
 *
 * @example
 *
 * ```ts
 * const d: GlyphDecimal = GlyphDecimalLegalTrademark.Copy; // 169 → "©"
 * String.fromCodePoint(d);
 * ```
 */
export type GlyphDecimal =
  | GlyphDecimalSpacingInvisible
  | GlyphDecimalDashesPunctuation
  | GlyphDecimalQuotations
  | GlyphDecimalReservedMarkup
  | GlyphDecimalLegalTrademark
  | GlyphDecimalCurrency
  | GlyphDecimalMathematical
  | GlyphDecimalArrows
  | GlyphDecimalCardSuits
  | GlyphDecimalGreekLowercase
  | GlyphDecimalGreekUppercase;

/**
 * Merged view of every {@link GlyphId*} string enum (`…SpacingInvisible`,
 * `…DashesPunctuation`, …). Keys are enum member names (`Nbsp`, `Mdash`, …); values
 * are HTML name tokens (`nbsp`, `mdash`, …). Used to build {@link GlyphsMap}.
 */
const GlyphIds = {
  ...GlyphIdSpacingInvisible,
  ...GlyphIdDashesPunctuation,
  ...GlyphIdQuotations,
  ...GlyphIdReservedMarkup,
  ...GlyphIdLegalTrademark,
  ...GlyphIdCurrency,
  ...GlyphIdMathematical,
  ...GlyphIdArrows,
  ...GlyphIdCardSuits,
  ...GlyphIdGreekLowercase,
  ...GlyphIdGreekUppercase,
} as const;

/**
 * Merged view of every {@link GlyphHex*} enum. Keys match {@link GlyphIds}; values
 * are hex digit strings (no `U+` / `&#x`). Used to build {@link GlyphsMap}.
 */
const GlyphHexes = {
  ...GlyphHexSpacingInvisible,
  ...GlyphHexDashesPunctuation,
  ...GlyphHexQuotations,
  ...GlyphHexReservedMarkup,
  ...GlyphHexLegalTrademark,
  ...GlyphHexCurrency,
  ...GlyphHexMathematical,
  ...GlyphHexArrows,
  ...GlyphHexCardSuits,
  ...GlyphHexGreekLowercase,
  ...GlyphHexGreekUppercase,
} as const;

/**
 * Merged view of every {@link GlyphDecimal*} enum. Keys match {@link GlyphIds};
 * values are numeric code points. Used to build {@link GlyphsMap}. Prefer
 * `Object.entries` with `typeof value === 'number'` if you need to skip numeric
 * enum reverse keys.
 */
const GlyphDecimals = {
  ...GlyphDecimalSpacingInvisible,
  ...GlyphDecimalDashesPunctuation,
  ...GlyphDecimalQuotations,
  ...GlyphDecimalReservedMarkup,
  ...GlyphDecimalLegalTrademark,
  ...GlyphDecimalCurrency,
  ...GlyphDecimalMathematical,
  ...GlyphDecimalArrows,
  ...GlyphDecimalCardSuits,
  ...GlyphDecimalGreekLowercase,
  ...GlyphDecimalGreekUppercase,
} as const;

/**
 * **Key:** alias key from {@link GlyphNames}; enum **member** name (`Nbsp`, …); or
 * the **canonical name token** (`nbsp`, …) from {@link GlyphIds}, so `id` can use
 * {@link GlyphId} values (e.g. `GlyphIdSpacingInvisible.Nbsp`).
 *
 * **Value:** HTML character reference from that row: named `&…;` (aliases and
 * {@link GlyphIds}), hex `&#x…;` ({@link GlyphHexes}), decimal `&#…;`
 * ({@link GlyphDecimals}). Decimal/hex scalars use numeric or hex-digit keys.
 */
export const GlyphsMap = new Map<string, string>([
  ...Object.entries(GlyphNames).map(
    ([key, token]) => [key, `&${token};`] as [string, string],
  ),
  ...Object.entries(GlyphIds).flatMap(([memberName, token]) => [
    [memberName, `&${token};`] as [string, string],
    [token, `&${token};`] as [string, string],
  ]),
  ...Object.entries(GlyphHexes).map(
    ([key, token]) => [key, `&#x${token};`] as [string, string],
  ),
  ...Object.entries(GlyphDecimals).map(
    ([key, token]) => [key, `&#${token};`] as [string, string],
  ),
]);
